<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else-if="run">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div class="mb-1">
            <h3 class="mb-50">
              {{ periodLabel }}
            </h3>
            <b-badge :variant="statusBadgeVariant(statusValue)">
              {{ statusText }}
            </b-badge>
          </div>
          <div class="d-none d-md-flex flex-wrap">
            <b-button
              v-if="canAccrue"
              variant="primary"
              class="rounded-pill mr-50 mb-50"
              :disabled="acting"
              @click="confirmAccrue"
            >
              {{ $t('ledger_payroll_accrue') }}
            </b-button>
            <b-button
              v-if="canPay"
              variant="success"
              class="rounded-pill mr-50 mb-50"
              :disabled="acting"
              @click="payOpen = true"
            >
              {{ $t('ledger_payroll_pay') }}
            </b-button>
            <b-button
              v-if="canCancel"
              variant="outline-danger"
              class="rounded-pill mb-50"
              :disabled="acting"
              @click="confirmCancel"
            >
              {{ $t('ledger_payroll_cancel') }}
            </b-button>
          </div>
        </div>

        <div class="ledger-payroll-totals mt-1">
          <div>
            <span class="text-muted">{{ $t('ledger_payroll_gross') }}</span>
            <strong class="ledger-num d-block">{{ money(totals.gross) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_payroll_deduction') }}</span>
            <strong class="ledger-num d-block">{{ money(totals.employee_deduction) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_payroll_employer_si') }}</span>
            <strong class="ledger-num d-block">{{ money(totals.employer_si) }}</strong>
          </div>
          <div>
            <span class="text-muted">{{ $t('ledger_payroll_net') }}</span>
            <strong class="ledger-num d-block">{{ money(totals.net) }}</strong>
          </div>
        </div>

        <div class="mt-1">
          <b-link
            v-if="accrualJournalId && canOpenJournal"
            class="d-inline-block mr-1"
            :to="journalRoute(accrualJournalId)"
          >
            {{ $t('ledger_payroll_accrual_journal') }}
          </b-link>
          <b-link
            v-if="paymentJournalId && canOpenJournal"
            class="d-inline-block"
            :to="journalRoute(paymentJournalId)"
          >
            {{ $t('ledger_payroll_payment_journal') }}
          </b-link>
        </div>

        <b-alert
          v-if="showFailuresLink"
          show
          variant="warning"
          class="mt-1 mb-0"
        >
          {{ $t('ledger_payroll_stays_draft') }}
          <b-link :to="{ name: 'ledger-posting-failures' }">
            {{ $t('ledger_failures_title') }}
          </b-link>
        </b-alert>
      </b-card>

      <b-card
        v-if="payOpen && canPay"
        class="rounded-lg"
      >
        <h5>{{ $t('ledger_payroll_pay') }}</h5>
        <p class="text-muted mb-1">
          {{ $t('ledger_payroll_pay_hint') }}
        </p>
        <b-form-group :label="$t('ledger_payroll_till')">
          <v-select
            v-model="payAccountId"
            :dir="isRtl ? 'rtl' : 'ltr'"
            :options="tills"
            :reduce="option => option.id"
            :get-option-label="tillLabel"
            :loading="loadingTills"
            :placeholder="$t('ledger_payroll_till')"
            class="ledger-account-select"
          />
        </b-form-group>
        <div class="d-flex">
          <b-button
            variant="outline-secondary"
            class="rounded-pill mr-50"
            @click="payOpen = false"
          >
            {{ $t('ledger_cancel') }}
          </b-button>
          <b-button
            variant="success"
            class="rounded-pill"
            :disabled="acting || !payAccountId"
            @click="confirmPay"
          >
            {{ $t('ledger_payroll_pay_confirm_btn') }}
          </b-button>
        </div>
      </b-card>

      <b-card class="rounded-lg">
        <div class="d-none d-md-block">
          <b-table
            :items="lines"
            :fields="fields"
            small
            responsive
            class="ledger-dense-table"
          >
            <template #cell(employee)="data">
              {{ employeeName(data.item) }}
            </template>
            <template #cell(basic_salary)="data">
              <b-form-input
                v-if="canEditLines"
                v-model.number="drafts[data.item.id].basic_salary"
                type="number"
                step="0.01"
                min="0"
              />
              <span
                v-else
                class="ledger-num"
              >{{ money(data.item.basic_salary) }}</span>
            </template>
            <template #cell(allowances)="data">
              <b-form-input
                v-if="canEditLines"
                v-model.number="drafts[data.item.id].allowances"
                type="number"
                step="0.01"
                min="0"
              />
              <span
                v-else
                class="ledger-num"
              >{{ money(data.item.allowances) }}</span>
            </template>
            <template #cell(employee_deduction)="data">
              <b-form-input
                v-if="canEditLines"
                v-model.number="drafts[data.item.id].employee_deduction"
                type="number"
                step="0.01"
                min="0"
              />
              <span
                v-else
                class="ledger-num"
              >{{ money(data.item.employee_deduction) }}</span>
            </template>
            <template #cell(employer_si)="data">
              <b-form-input
                v-if="canEditLines"
                v-model.number="drafts[data.item.id].employer_si"
                type="number"
                step="0.01"
                min="0"
              />
              <span
                v-else
                class="ledger-num"
              >{{ money(data.item.employer_si) }}</span>
            </template>
            <template #cell(project)="data">
              <payroll-project-select
                v-if="canEditLines"
                :value="drafts[data.item.id].project_id"
                :project="data.item.project"
                :placeholder="$t('ledger_payroll_project')"
                @input="id => drafts[data.item.id].project_id = id"
              />
              <span v-else>{{ projectLabel(data.item) }}</span>
            </template>
            <template #cell(gl_activity)="data">
              <b-form-select
                v-if="canEditLines"
                v-model="drafts[data.item.id].gl_activity"
                :options="activityOptions"
              />
              <span v-else>{{ activityLabel(data.item.gl_activity) }}</span>
            </template>
            <template #cell(gross)="data">
              <span class="ledger-num">{{ money(data.item.gross) }}</span>
            </template>
            <template #cell(net)="data">
              <span class="ledger-num">{{ money(data.item.net) }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                v-if="canEditLines"
                size="sm"
                variant="primary"
                :disabled="savingId === data.item.id"
                @click="saveLine(data.item)"
              >
                {{ $t('ledger_payroll_save_line') }}
              </b-button>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <article
            v-for="line in lines"
            :key="line.id"
            class="ledger-payroll-line"
          >
            <strong>{{ employeeName(line) }}</strong>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_basic')"
            >
              <b-form-input
                v-model.number="drafts[line.id].basic_salary"
                type="number"
                step="0.01"
                min="0"
              />
            </b-form-group>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_allowances')"
            >
              <b-form-input
                v-model.number="drafts[line.id].allowances"
                type="number"
                step="0.01"
                min="0"
              />
            </b-form-group>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_deduction')"
            >
              <b-form-input
                v-model.number="drafts[line.id].employee_deduction"
                type="number"
                step="0.01"
                min="0"
              />
            </b-form-group>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_employer_si')"
            >
              <b-form-input
                v-model.number="drafts[line.id].employer_si"
                type="number"
                step="0.01"
                min="0"
              />
            </b-form-group>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_project')"
            >
              <payroll-project-select
                :value="drafts[line.id].project_id"
                :project="line.project"
                :placeholder="$t('ledger_payroll_project')"
                @input="id => drafts[line.id].project_id = id"
              />
            </b-form-group>
            <b-form-group
              v-if="canEditLines"
              :label="$t('ledger_payroll_activity')"
            >
              <b-form-select
                v-model="drafts[line.id].gl_activity"
                :options="activityOptions"
              />
            </b-form-group>
            <div
              v-if="!canEditLines"
              class="small text-muted mt-50"
            >
              {{ projectLabel(line) }}
              <span v-if="line.gl_activity"> · {{ activityLabel(line.gl_activity) }}</span>
            </div>
            <div class="d-flex justify-content-between ledger-num mt-50">
              <span>{{ $t('ledger_payroll_gross') }} {{ money(line.gross) }}</span>
              <span>{{ $t('ledger_payroll_net') }} {{ money(line.net) }}</span>
            </div>
            <b-button
              v-if="canEditLines"
              block
              variant="primary"
              class="mt-1"
              :disabled="savingId === line.id"
              @click="saveLine(line)"
            >
              {{ $t('ledger_payroll_save_line') }}
            </b-button>
          </article>
        </div>
      </b-card>

      <div
        v-if="canPay || canAccrue || canCancel"
        class="ledger-payroll-sticky d-md-none"
      >
        <div class="ledger-num small mb-50">
          {{ $t('ledger_payroll_net') }} {{ money(totals.net) }}
        </div>
        <b-button
          v-if="canPay"
          block
          variant="success"
          class="rounded-pill"
          @click="payOpen = true"
        >
          {{ $t('ledger_payroll_pay') }}
        </b-button>
        <b-button
          v-else-if="canAccrue"
          block
          variant="primary"
          class="rounded-pill"
          :disabled="acting"
          @click="confirmAccrue"
        >
          {{ $t('ledger_payroll_accrue') }}
        </b-button>
        <b-button
          v-if="canCancel"
          block
          variant="outline-danger"
          class="rounded-pill mt-50"
          :disabled="acting"
          @click="confirmCancel"
        >
          {{ $t('ledger_payroll_cancel') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BLink, BAlert, BTable, BFormInput, BFormSelect, BFormGroup, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getPayrollRun, updatePayrollLine, accruePayrollRun, payPayrollRun, cancelPayrollRun,
} from '@/api/accounting/payrollRuns'
import { fetchAllOperationalAccounts } from '@/api/accounting/accounts'
import PayrollProjectSelect from '@/views/modules/ledger/components/PayrollProjectSelect.vue'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, roundMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

const ACTIVITIES = ['survey', 'contracting', 'licensing', 'maps']

export default {
  name: 'PayrollRunShow',
  components: {
    BCard,
    BBadge,
    BButton,
    BLink,
    BAlert,
    BTable,
    BFormInput,
    BFormSelect,
    BFormGroup,
    BSpinner,
    vSelect,
    PayrollProjectSelect,
  },
  data() {
    return {
      loading: false,
      acting: false,
      savingId: null,
      payOpen: false,
      loadingTills: false,
      tills: [],
      payAccountId: null,
      showFailuresLink: false,
      run: null,
      drafts: {},
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    statusValue() {
      return enumValue(this.run && this.run.status)
    },
    statusText() {
      return enumLabel(this.run && this.run.status, this.$i18n.locale)
    },
    totals() {
      return (this.run && this.run.totals) || {}
    },
    lines() {
      return (this.run && this.run.lines) || []
    },
    canEditLines() {
      return this.statusValue === 'draft' && this.hasPermission('edit payroll')
    },
    canAccrue() {
      return this.statusValue === 'draft' && this.hasPermission('post payroll')
    },
    canPay() {
      return this.statusValue === 'accrued' && this.hasPermission('post payroll')
    },
    canCancel() {
      return (this.statusValue === 'draft' || this.statusValue === 'accrued')
        && this.hasPermission('edit payroll')
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    accrualJournalId() {
      return (this.run && (this.run.accrual_journal_entry_id
        || (this.run.accrual_journal_entry && this.run.accrual_journal_entry.id))) || null
    },
    paymentJournalId() {
      return (this.run && (this.run.payment_journal_entry_id
        || (this.run.payment_journal_entry && this.run.payment_journal_entry.id))) || null
    },
    periodLabel() {
      if (!this.run) return ''
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      const month = new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, this.run.month - 1, 1))
      return `${month} ${this.run.year}`
    },
    activityOptions() {
      return [
        { value: '', text: this.$t('ledger_payroll_activity_none') },
        ...ACTIVITIES.map(value => ({
          value,
          text: this.$t(`ledger_payroll_activity_${value}`),
        })),
      ]
    },
    fields() {
      const cols = [
        { key: 'employee', label: this.$t('ledger_employee') },
        { key: 'basic_salary', label: this.$t('ledger_payroll_basic') },
        { key: 'allowances', label: this.$t('ledger_payroll_allowances') },
        { key: 'employee_deduction', label: this.$t('ledger_payroll_deduction') },
        { key: 'employer_si', label: this.$t('ledger_payroll_employer_si') },
        { key: 'project', label: this.$t('ledger_payroll_project') },
        { key: 'gl_activity', label: this.$t('ledger_payroll_activity') },
        { key: 'gross', label: this.$t('ledger_payroll_gross') },
        { key: 'net', label: this.$t('ledger_payroll_net') },
      ]
      if (this.canEditLines) cols.push({ key: 'actions', label: this.$t('ledger_actions') })
      return cols
    },
  },
  watch: {
    '$route.params.id': 'loadRun',
    canPay(value) {
      if (value) this.loadTills()
    },
  },
  mounted() {
    this.loadRun()
  },
  methods: {
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    employeeName(line) {
      const employee = line && line.employee
      if (employee && typeof employee === 'object') {
        return employee.full_name || employee.name || employee.name_ar || `#${line.employee_id}`
      }
      return line && line.employee_id ? `#${line.employee_id}` : ''
    },
    projectLabel(line) {
      const project = line && line.project
      if (!project) return '—'
      return project.name || project.title || project.code || `#${project.id}`
    },
    activityLabel(value) {
      const key = enumValue(value)
      if (!key) return this.$t('ledger_payroll_activity_none')
      const i18nKey = `ledger_payroll_activity_${key}`
      return this.$te(i18nKey) ? this.$t(i18nKey) : key
    },
    tillLabel(account) {
      if (!account) return ''
      const code = account.code ? `${account.code} — ` : ''
      return `${code}${account.name || ''}`
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    toast(title, variant) {
      const icons = {
        success: 'CheckIcon',
        warning: 'AlertTriangleIcon',
        danger: 'AlertCircleIcon',
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: icons[variant] || 'InfoIcon',
          variant,
        },
      })
    },
    syncDrafts(run) {
      const drafts = {}
      const lines = run.lines || []
      lines.forEach(line => {
        drafts[line.id] = {
          basic_salary: Number(line.basic_salary) || 0,
          allowances: Number(line.allowances) || 0,
          employee_deduction: Number(line.employee_deduction) || 0,
          employer_si: Number(line.employer_si) || 0,
          project_id: line.project_id == null ? null : Number(line.project_id),
          gl_activity: enumValue(line.gl_activity) || '',
        }
      })
      this.drafts = drafts
    },
    async loadRun(options = {}) {
      const silent = options.silent === true
      if (!silent) {
        this.loading = true
        this.showFailuresLink = false
      }
      try {
        const res = await getPayrollRun(this.$route.params.id)
        this.run = oneFrom(res)
        this.syncDrafts(this.run || {})
        if (this.canPay) this.loadTills()
      } catch (error) {
        if (!silent) this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadTills() {
      if (this.tills.length || this.loadingTills) return
      this.loadingTills = true
      try {
        const rows = await fetchAllOperationalAccounts({ status: 'active', per_page: 100 })
        this.tills = rows.filter(account => {
          const type = enumValue(account.type)
          return type === 'cash' || type === 'bank' || type === 'petty_cash'
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingTills = false
      }
    },
    linePayload(line) {
      const draft = this.drafts[line.id]
      return {
        basic_salary: roundMoney(draft.basic_salary),
        allowances: roundMoney(draft.allowances),
        employee_deduction: roundMoney(draft.employee_deduction),
        employer_si: roundMoney(draft.employer_si),
        project_id: draft.project_id || null,
        gl_activity: draft.gl_activity || null,
      }
    },
    async saveLine(line) {
      if (!this.canEditLines) return
      this.savingId = line.id
      try {
        await updatePayrollLine(this.run.id, line.id, this.linePayload(line))
        const res = await getPayrollRun(this.run.id)
        this.run = oneFrom(res)
        this.syncDrafts(this.run || {})
        this.toast(this.$t('ledger_payroll_line_saved'), 'success')
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.savingId = null
      }
    },
    async confirmAccrue() {
      if (!this.canAccrue || this.acting) return
      const result = await Swal.fire({
        text: this.$t('ledger_payroll_accrue_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_payroll_accrue'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      this.showFailuresLink = false
      try {
        const res = await accruePayrollRun(this.run.id)
        const updated = oneFrom(res)
        const journalId = (updated && (updated.accrual_journal_entry_id
          || (updated.accrual_journal_entry && updated.accrual_journal_entry.id)))
          || this.accrualJournalId
        this.toast(this.$t('ledger_payroll_accrued'), 'success')
        if (journalId && this.canOpenJournal) {
          this.$router.push(this.journalRoute(journalId))
          return
        }
        await this.loadRun()
      } catch (error) {
        const status = error?.response?.status
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), status === 422 ? 'warning' : 'danger')
        if (status === 422) {
          await this.loadRun({ silent: true })
          this.showFailuresLink = this.hasPermission('view gl_posting_failures')
        }
      } finally {
        this.acting = false
      }
    },
    async confirmPay() {
      if (!this.canPay || this.acting || !this.payAccountId) return
      const result = await Swal.fire({
        title: this.$t('ledger_payroll_pay'),
        text: this.$t('ledger_payroll_pay_confirm', { net: this.money(this.totals.net) }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_payroll_pay'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        const res = await payPayrollRun(this.run.id, { account_id: this.payAccountId })
        const updated = oneFrom(res)
        const journalId = updated && (updated.payment_journal_entry_id
          || (updated.payment_journal_entry && updated.payment_journal_entry.id))
        this.toast(this.$t('ledger_payroll_paid'), 'success')
        if (journalId && this.canOpenJournal) {
          this.$router.push(this.journalRoute(journalId))
          return
        }
        this.payOpen = false
        await this.loadRun()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.acting = false
      }
    },
    async confirmCancel() {
      if (!this.canCancel || this.acting) return
      const text = this.statusValue === 'accrued'
        ? this.$t('ledger_payroll_cancel_accrued')
        : this.$t('ledger_payroll_cancel_draft')
      const result = await Swal.fire({
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_payroll_cancel'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        await cancelPayrollRun(this.run.id)
        this.toast(this.$t('ledger_payroll_cancelled'), 'success')
        await this.loadRun()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-payroll-totals {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
@media (min-width: 768px) {
  .ledger-payroll-totals {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.ledger-payroll-line {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
}
.ledger-payroll-sticky {
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 20;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
</style>
